import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Video, Search, Filter, Folder, LogOut, User, Calendar, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_videogist-3/artifacts/809w15is_summbee%20logo_just%20bee.png";

function LibraryPage() {
  const { user, subscription, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectFilter = searchParams.get("project");

  const [videos, setVideos] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProject, setSelectedProject] = useState(projectFilter || "all");

  const canUseLibrary = subscription?.effective_plan && 
    (subscription.effective_plan === "pro" || subscription.effective_plan === "creator" || subscription.in_trial);

  useEffect(() => {
    if (!canUseLibrary) {
      navigate("/pricing");
      return;
    }
    fetchProjects();
    fetchVideos();
  }, [canUseLibrary, selectedProject]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`, { withCredentials: true });
      setProjects(response.data.projects || []);
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  const fetchVideos = async () => {
    setLoading(true);
    try {
      const url = selectedProject && selectedProject !== "all" 
        ? `${API}/videos?project_id=${selectedProject}`
        : `${API}/videos`;
      
      const response = await axios.get(url, { withCredentials: true });
      setVideos(response.data.videos || []);
    } catch (error) {
      console.error("Error fetching videos:", error);
      if (error.response?.status === 403) {
        navigate("/pricing");
      } else {
        toast.error("Failed to load videos");
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredVideos = videos.filter((video) =>
    video.video_title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getProjectsForVideo = (videoProjectIds) => {
    return projects.filter((p) => videoProjectIds.includes(p.id));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { 
      month: "short", 
      day: "numeric", 
      year: "numeric" 
    });
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="loading-state">Loading...</div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <nav className="navbar">
        <div className="nav-content">
          <div className="nav-logo" onClick={() => navigate("/")} style={{cursor: "pointer"}}>
            <img src={LOGO_URL} alt="SummBee" className="logo-image" />
            <span>SummBee</span>
          </div>
          <div className="nav-links">
            <Button onClick={() => navigate("/dashboard")} variant="ghost">
              Dashboard
            </Button>
            <Button onClick={() => navigate("/library")} variant="ghost" className="active">
              My Videos
            </Button>
            <Button onClick={() => navigate("/projects")} variant="ghost">
              Projects
            </Button>
            <div className="user-info">
              <User size={16} />
              <span>{user?.name}</span>
            </div>
            <Button onClick={logout} variant="ghost" className="logout-button">
              <LogOut size={16} /> Logout
            </Button>
          </div>
        </div>
      </nav>

      <div className="dashboard-container">
        <div className="page-header">
          <div>
            <h1 className="page-title">My Videos</h1>
            <p className="page-subtitle">Browse and manage your video library</p>
          </div>
        </div>

        {/* Filters */}
        <div className="library-filters">
          <div className="search-box">
            <Search size={20} className="search-icon" />
            <Input
              placeholder="Search videos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="project-filter">
            <Filter size={20} />
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Projects</option>
              <option value="">Uncategorized</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {filteredVideos.length === 0 ? (
          <Card className="empty-state-card">
            <CardContent className="empty-state">
              <Video size={64} className="empty-icon" />
              <h3>No videos yet</h3>
              <p>Process your first video to build your library</p>
              <Button onClick={() => navigate("/dashboard")} className="create-button">
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="videos-list">
            {filteredVideos.map((video) => {
              const videoProjects = getProjectsForVideo(video.project_ids || []);
              return (
                <Card key={video.id} className="video-card">
                  <CardHeader>
                    <div className="video-header">
                      <div className="video-info">
                        <CardTitle className="video-title">{video.video_title}</CardTitle>
                        <div className="video-meta">
                          <span className="platform-badge">{video.platform}</span>
                          <span className="video-date">
                            <Calendar size={14} />
                            {formatDate(video.created_at)}
                          </span>
                        </div>
                      </div>
                      <a
                        href={video.video_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="external-link"
                      >
                        <ExternalLink size={20} />
                      </a>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {videoProjects.length > 0 && (
                      <div className="video-projects">
                        {videoProjects.map((project) => (
                          <span
                            key={project.id}
                            className="project-badge"
                            style={{ 
                              backgroundColor: project.color + "20",
                              color: project.color,
                              borderColor: project.color
                            }}
                          >
                            <Folder size={14} />
                            {project.name}
                          </span>
                        ))}
                      </div>
                    )}
                    {video.bullet_summary && (
                      <div className="video-summary">
                        <p className="summary-label">Key Points:</p>
                        <p className="summary-text">{video.bullet_summary.substring(0, 200)}...</p>
                      </div>
                    )}
                    <div className="video-actions">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          // TODO: Open video details modal
                          toast.info("Video details coming soon");
                        }}
                      >
                        View Details
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default LibraryPage;
